// src/pages/Affordable.js
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const ITEMS_PER_BATCH = 20;

const Affordable = () => {
  const [allProducts, setAllProducts] = useState([]);
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_BATCH);

  // فیلترها
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('همه');
  const [sortBy, setSortBy] = useState('price_asc');
  const [types, setTypes] = useState(['همه']);

  const [filteredTotal, setFilteredTotal] = useState(0); // جدید: تعداد واقعی بعد از فیلتر

  const loaderRef = useRef(null);
  const observerRef = useRef(null);

  // لود محصولات
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await axios.get('/products?eco=0');
        const prods = res.data || [];
        console.log('تعداد کل محصولات دریافتی:', prods.length);
        setAllProducts(prods);

        const uniqueTypes = ['همه', ...new Set(prods.map(p => p.noe_kala || '').filter(Boolean))];
        setTypes(uniqueTypes);
      } catch (error) {
        console.error('خطا در دریافت محصولات:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // ریست visibleCount هنگام تغییر فیلتر/جستجو/مرتب‌سازی
  useEffect(() => {
    setVisibleCount(ITEMS_PER_BATCH);
  }, [searchTerm, selectedType, sortBy]);

  // فیلتر + مرتب‌سازی + برش لیست
  useEffect(() => {
    if (allProducts.length === 0) return;

    let filtered = [...allProducts];

    // جستجو
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(p =>
        (p.name || '').toLowerCase().includes(term) ||
        (p.barcode || '').includes(term)
      );
    }

    // نوع کالا
    if (selectedType !== 'همه') {
      filtered = filtered.filter(p => p.noe_kala === selectedType);
    }

    // مرتب‌سازی
    switch (sortBy) {
      case 'price_asc':
        filtered.sort((a, b) => (a.final_price || 0) - (b.final_price || 0));
        break;
      case 'price_desc':
        filtered.sort((a, b) => (b.final_price || 0) - (a.final_price || 0));
        break;
      case 'darsad_asc':
        filtered.sort((a, b) => (a.darsad || 0) - (b.darsad || 0));
        break;
      case 'darsad_desc':
        filtered.sort((a, b) => (b.darsad || 0) - (a.darsad || 0));
        break;
      default:
        filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }

    // ذخیره تعداد کل بعد از فیلتر
    setFilteredTotal(filtered.length);

    // نمایش بخشی از لیست فیلتر شده
    setDisplayedProducts(filtered.slice(0, visibleCount));
  }, [allProducts, searchTerm, selectedType, sortBy, visibleCount]);

  // Intersection Observer برای لود بیشتر
  useEffect(() => {
    // اگر همه موارد فیلترشده نمایش داده شده‌اند → observer را خاموش کن
    if (displayedProducts.length >= filteredTotal || filteredTotal === 0) {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          console.log('اسکرول به loader رسید → افزایش visibleCount');
          setVisibleCount(prev => prev + ITEMS_PER_BATCH);
        }
      },
      {
        rootMargin: '400px 0px 400px 0px',
        threshold: 0.1,
      }
    );

    observerRef.current = observer;

    const tryObserve = () => {
      if (loaderRef.current) {
        console.log('loaderRef پیدا شد → observer فعال');
        observer.observe(loaderRef.current);
      } else {
        console.log('loaderRef هنوز موجود نیست');
      }
    };

    tryObserve();

    // تلاش مجدد اگر DOM هنوز آماده نیست
    const interval = setInterval(() => {
      if (loaderRef.current) {
        tryObserve();
        clearInterval(interval);
      }
    }, 800);

    return () => {
      clearInterval(interval);
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [displayedProducts.length, filteredTotal, visibleCount]); // وابستگی مناسب

  const seperator = (rial) => {
    if (!rial && rial !== 0) return '۰';
    return Math.round(rial).toLocaleString('fa-IR');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl text-gold animate-pulse">در حال بارگذاری محصولات...</div>
      </div>
    );
  }

  const hasMore = displayedProducts.length < filteredTotal;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gold-light/20 via-white to-gold-light/20 py-12 px-4 flex flex-col">
      <div className="max-w-7xl mx-auto flex-grow">
        <h1 className="text-4xl md:text-5xl font-bold text-gold-dark text-center mb-12">
          محصولات اقتصادی خرمی‌گلد
        </h1>

        {/* فیلترها */}
        <div className="bg-white rounded-3xl shadow-xl p-6 mb-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-gray-700 font-medium mb-2">جستجو</label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="نام محصول یا بارکد..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-3 pr-12 border border-gold/30 rounded-xl focus:outline-none focus:border-gold"
                />
                <button className="absolute left-3 top-1/2 -translate-y-1/2 text-gold">🔍</button>
              </div>
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-2">نوع کالا</label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full px-4 py-3 border border-gold/30 rounded-xl focus:outline-none focus:border-gold"
              >
                {types.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-2">مرتب‌سازی</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-4 py-3 border border-gold/30 rounded-xl focus:outline-none focus:border-gold"
              >
                <option value="name">نام محصول</option>
                <option value="price_asc">قیمت: کم به زیاد</option>
                <option value="price_desc">قیمت: زیاد به کم</option>
                <option value="darsad_asc">اجرت: کم به زیاد</option>
                <option value="darsad_desc">اجرت: زیاد به کم</option>
              </select>
            </div>
          </div>
        </div>

        <p className="text-center text-gray-600 mb-8 font-medium">
          {displayedProducts.length} محصول نمایش داده شده (از {filteredTotal} مورد پس از فیلتر)
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {displayedProducts.map(product => (
            <Link
              key={product.id}
              to={`/product/${product.barcode}`}
              className="bg-white rounded-3xl shadow-xl overflow-hidden hover:shadow-2xl transition transform hover:-translate-y-2 group"
            >
              <div className="relative h-80 overflow-hidden bg-gradient-to-br from-gray-100 to-gray-300">
                {product.images && product.images.length > 0 ? (
                  <img
                    src={product.images.find(img => img.is_main)?.image_url || product.images[0].image_url}
                    alt={product.name}
                    className="w-full h-full object-contain object-center"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    بدون عکس
                  </div>
                )}
                {product.mojood === 0 && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                    <span className="text-white text-2xl font-bold">ناموجود</span>
                  </div>
                )}
              </div>

              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-2 line-clamp-2">{product.name}</h3>
                <p className="text-sm text-gray-600 mb-2">بارکد: {product.barcode}</p>
                <p className="text-sm text-gray-600 mb-4">نوع: {product.noe_kala || 'نامشخص'}</p>

                <div className="space-y-2 text-sm mb-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">وزن:</span>
                    <span className="font-medium">{product.vazn} گرم</span>
                  </div>
                  {product.darsad > -1 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">اجرت:</span>
                      <span className="font-medium">{product.darsad}%</span>
                    </div>
                  )}
                  {product.sood > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">سود:</span>
                      <span className="font-medium">{product.sood}%</span>
                    </div>
                  )}
                </div>

                <div className="pt-4 border-t border-gray-200">
                  {product.price_before_discount > product.final_price && (
                    <p className="text-lg text-gray-500 line-through text-center mb-2">
                      {seperator(product.price_before_discount)} تومان
                    </p>
                  )}
                  <p className="text-2xl font-bold text-gold-dark text-center">
                    {seperator(product.final_price)} تومان
                  </p>
                </div>

                <button className="w-full mt-6 bg-gold hover:bg-gold-dark text-white font-bold py-3 rounded-xl transition">
                  مشاهده جزئیات
                </button>
              </div>
            </Link>
          ))}
        </div>

        {/* Loader / پیام پایان */}
        <div
          ref={loaderRef}
          className="h-96 min-h-[40vh] flex items-center justify-center mt-16 border-t-2 border-gold/30"
        >
          {hasMore ? (
            <div className="text-3xl text-gold animate-pulse flex items-center gap-4">
              <span className="animate-spin">↻</span> در حال بارگذاری بیشتر...
            </div>
          ) : (
            <div className="text-xl text-gray-600 py-12 font-medium text-center">
              {selectedType === 'همه'
                ? 'همه محصولات نمایش داده شد ✓'
                : `همه محصولات ${selectedType} نمایش داده شد (${filteredTotal} مورد) ✓`}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Affordable;